/* eslint-disable camelcase -- backend wire-format field names */
import React, { useState } from 'react'
import { Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react'
import type { HeardAboutAnswer } from '../../services/heardAboutService'

const HEARD_ABOUT_OPTIONS = [
  { id: 'search', label: 'Search engine' },
  { id: 'social', label: 'Social media' },
  { id: 'friend_family', label: 'Friend or family' },
  { id: 'youtube_podcast', label: 'YouTube or a podcast' },
  { id: 'practitioner', label: 'My doctor or practitioner' },
  { id: 'article_press', label: 'An article or news' },
  { id: 'ad', label: 'An ad' },
  { id: 'other', label: 'Something else' },
] as const

const OTHER_TEXT_MAX = 80

interface HeardAboutChipsProps {
  /** Persist the answer. Failures are swallowed: the question never blocks the page. */
  onSubmit: (answer: HeardAboutAnswer) => Promise<void>
  /** Called after an answer is sent or the question is skipped. */
  onDone: () => void
}

export const HeardAboutChips: React.FC<HeardAboutChipsProps> = ({ onSubmit, onDone }) => {
  const [showOther, setShowOther] = useState(false)
  const [otherText, setOtherText] = useState('')
  const [sending, setSending] = useState(false)

  const send = async (answer: HeardAboutAnswer) => {
    setSending(true)
    try {
      await onSubmit(answer)
    } catch {
      // Attribution is best effort; never surface an error for it.
    }
    onDone()
  }

  const handleChip = (id: string) => {
    if (id === 'other') {
      setShowOther(true)
      return
    }
    void send({ choice: id })
  }

  const trimmed = otherText.trim()

  return (
    <VStack align="start" gap={2} w="full" data-testid="heard-about">
      <HStack justify="space-between" w="full" align="start">
        <Box>
          <Text fontWeight="600" fontSize="sm">Help us reach more people like you</Text>
          <Text fontSize="xs" color="fg.muted">How did you find Livewello?</Text>
        </Box>
        <Button variant="ghost" size="xs" color="fg.muted" onClick={onDone} disabled={sending}>
          Skip
        </Button>
      </HStack>
      <HStack gap={2} wrap="wrap">
        {HEARD_ABOUT_OPTIONS.map((o) => (
          <Button
            key={o.id}
            size="xs"
            variant={o.id === 'other' && showOther ? 'solid' : 'outline'}
            colorPalette="brand"
            borderRadius="full"
            disabled={sending}
            onClick={() => handleChip(o.id)}
          >
            {o.label}
          </Button>
        ))}
      </HStack>
      {showOther && (
        <HStack w="full" gap={2}>
          <Input
            size="sm"
            placeholder="Tell us where"
            aria-label="Where did you find Livewello?"
            maxLength={OTHER_TEXT_MAX}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value.slice(0, OTHER_TEXT_MAX))}
          />
          <Button
            size="sm"
            colorPalette="brand"
            disabled={sending || !trimmed}
            onClick={() => void send({ choice: 'other', other_text: trimmed })}
          >
            Send
          </Button>
        </HStack>
      )}
    </VStack>
  )
}
